import { Component, computed, DestroyRef, inject, OnInit, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AppInfo } from '../../../../../../../global/environment';
import { FeatureFlags } from '../../../../../../../global/featureFlags/featureFlags';
import { SmartFixSolution, Tracking } from '../../../../../../../global/tracking/tracking';
import { ButtonComponent } from '../../../../../../../shared/components/button/button.component';
import { DialogService } from '../../../../../../../shared/components/dialog/services/dialog.service';
import { LoadingSpinnerComponent } from '../../../../../../../shared/components/forms/loading-spinner/loading-spinner.component';
import { Breakpoints } from '../../../../../../../shared/types/breakpoints';
import { ORBIT_BUNDLE, ORBIT_BUNDLE_SESSION_KEY } from '../../../../../constants';
import { OrbitFormClaimService } from '../../../../services/orbit-form-claim.service';
import { SmartFixClient } from './headless-smart-fix/headless-smartfix';
import { SmartFixChatComponent } from './smart-fix-chat/smart-fix-chat.component';
import { SmartFixFeedbackComponent } from './smart-fix-feedback/smart-fix-feedback.component';
import { SmartFixImageHeroComponent } from './smart-fix-image-hero/smart-fix-image-hero.component';
import {
  DialogAction,
  InteractionMode,
  Voice2VoiceDialogComponent,
} from './smart-fix-voice2voice-dialogs/voice2voice-dialogs-component.component';
import { SmartFixAnalyticsService } from './utils/analytics/smart-fix-analytics-service';
import { scrollInUp, slideInOut } from './utils/animations/chatAnimations';
import { SMART_FIX_HINTS, SMART_FIX_INCENTIVES } from './utils/constants/smart-fix-constants';
import { mergeConsecutiveBy, normaliseBrandName } from './utils/helpers/bedrock-messages-helper';
import {
  BedrockChatSession,
  BedrockMessages,
  SmartFixUIState,
  VoiceChatSession,
} from './utils/interfaces/bedrock-chat.interface';
import { NovaSonicVoiceService } from './utils/nova-sonic-voice.service';
import { SmartFixToggleComponent } from './utils/smart-fix-solution-toggle/smart-fix-solution-toggle.component';

@Component({
  selector: 'app-smart-fix',
  imports: [
    ButtonComponent,
    FormsModule,
    ReactiveFormsModule,
    SmartFixFeedbackComponent,
    SmartFixChatComponent,
    SmartFixToggleComponent,
    SmartFixImageHeroComponent,
    LoadingSpinnerComponent,
  ],
  templateUrl: './smart-fix.component.html',
  styleUrl: './smart-fix.component.scss',
  animations: [scrollInUp, slideInOut],
})
export class SmartFixComponent implements OnInit {
  desktopHero = viewChild<SmartFixImageHeroComponent>('desktopHero');
  mobileHero = viewChild<SmartFixImageHeroComponent>('mobileHero');

  readonly incentives = SMART_FIX_INCENTIVES;
  hints = SMART_FIX_HINTS;
  public SmartFixUIState = SmartFixUIState;

  chatboxActive: boolean = false;
  chatMessage: string | any = '';
  chatSession: BedrockChatSession | undefined = undefined;
  voiceChatSession: VoiceChatSession | undefined = undefined;
  interactionStarted: boolean = true;
  askForFeedback: boolean = false;
  feedbackRequested: boolean = false;
  feedbackText: string = '';
  isWarningDialog = false;
  planNumber: string = '';
  answers: { question: string; answer: string }[] = [];
  solutionSelected: SmartFixSolution | undefined = undefined;
  interactionModeSelected: 'Voice to Voice' | 'Text' = 'Text';
  showGreeting: boolean = false;

  isFeedbackMode: boolean = false;
  MMRlink: string = '';

  chatLoading = signal(false);
  chatHistory = signal<BedrockMessages[]>([]);
  sessionId = signal('');
  isFirstInteraction = signal(false);
  userMessageCount = signal(0);

  /** Caches base64 audio keyed by messageId until the matching chat message is updated. */
  private readonly audioCache = new Map<string, string>();

  currentPrompt = '';
  gatheringContextActive = false;
  summary = '';
  showImagePrompt = false;
  feedbackActive = false;
  transitionPlaying: boolean = false;
  showSafetyCheck = false;
  isSwitchingFromVoiceToText = false;
  waitingForFirstNovaSonicResponse = false;
  private novaSonicInitErrorDialogOpen = false;
  private novaSonicInitFailureHandled = false;
  private novaSonicInitErrorDialogRef: { close: (result?: unknown) => void } | null = null;
  private novaSonicInitErrorAutoSwitchTimer: ReturnType<typeof setTimeout> | null = null;

  showSolutionToggle = AppInfo.Environment?.smartFixFeatures?.showSmartFixToggleSolution;
  agenticSolutionActiveFlag = FeatureFlags.getFeatureFlag('smart-fix-gen-ai-agentic');
  heatingAgenticSolutionActiveFlag = FeatureFlags.getFeatureFlag('smart-fix-gen-ai-boilers');
  tvAgenticSolutionActiveFlag = FeatureFlags.getFeatureFlag('smart-fix-gen-ai-tvs');
  vacuumAgenticSolutionActiveFlag = FeatureFlags.getFeatureFlag('smart-fix-gen-ai-vacuums');

  protected viewModel = computed(() => {
    const rawHistory = this.chatHistory();
    const isVoiceToVoice = this.interactionModeSelected === 'Voice to Voice';
    const chatHistory = isVoiceToVoice
      ? mergeConsecutiveBy(
          rawHistory,
          (m) => m.messageId ?? m.role,
          (a, b) => ({ ...a, content: { ...a.content, message: a.content.message + ' ' + b.content.message } })
        )
      : rawHistory;

    const bundle = this.orbitClaimService?.state?.claimData?.bundle;
    return {
      isLoading: this.orbitClaimService?.state.submittingValue,
      bundle: this.orbitClaimService?.state?.claimData,
      chatLoading: this.chatLoading(),
      chatHistory,
      sessionId: this.sessionId(),
      productType: bundle?.productType,
      isHeatingPlan: bundle?.goodsColour === 'H',
      isWhiteGoodPlan: bundle?.goodsColour === 'W',
      isTV: bundle?.isTV,
      isVacuum: bundle?.isFloorCareProduct,
      brand: normaliseBrandName(this.orbitClaimService?.state?.claimData?.bundle?.manufacturer),
    };
  });

  get vm() {
    return this.viewModel();
  }

  get shouldUseAgenticSolution(): boolean {
    const bundle = this.orbitClaimService?.state?.claimData?.bundle;
    if (bundle?.goodsColour === 'H') return this.heatingAgenticSolutionActiveFlag;
    if (bundle?.isTV) return this.tvAgenticSolutionActiveFlag;
    if (bundle?.isFloorCareProduct) return this.vacuumAgenticSolutionActiveFlag;
    return this.agenticSolutionActiveFlag;
  }

  // Voice to voice mode restricted to FF and whitegoods only
  get showInteractionModeDialog(): boolean {
    return FeatureFlags.getFeatureFlag('smart-fix-interaction-mode-selection') && this.vm.isWhiteGoodPlan;
  }

  // used only for local debug reasons
  private logSmartFixDebug(stage: string, solution: string): void {
    console.log('[SmartFix][debug]', {
      stage,
      productType: this.vm.productType,
      bundleFlags: {
        isTV: this.vm.bundle?.bundle?.isTV,
        isFloorCareProduct: this.vm.bundle?.bundle?.isFloorCareProduct,
        goodsColour: this.vm.bundle?.bundle?.goodsColour,
      },
      computedFlags: {
        isHeatingPlan: this.vm.isHeatingPlan,
        isTV: this.vm.isTV,
        isVacuum: this.vm.isVacuum,
      },
      featureFlags: {
        default: this.agenticSolutionActiveFlag,
        heating: this.heatingAgenticSolutionActiveFlag,
        tv: this.tvAgenticSolutionActiveFlag,
        vacuum: this.vacuumAgenticSolutionActiveFlag,
      },
      selectedSolution: solution,
    });
  }

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly orbitClaimService: OrbitFormClaimService,
    private readonly route: ActivatedRoute,
    private smartFixAnalyticsService: SmartFixAnalyticsService,
    private dialogService: DialogService,
    private novaSonicVoiceService: NovaSonicVoiceService
  ) {}

  ngOnInit(): void {
    this.planNumber = this.route.snapshot.params['planNumber'];
    this.MMRlink = `${AppInfo.Environment.claimsURL}/my-plans/${this.planNumber}/claims`;

    this.listenForNovaSonicModelTimeout();
    // LiveChat.hideLiveChatLauncher();

    this.solutionSelected = this.shouldUseAgenticSolution ? 'agent-llm' : 'rag';

    Tracking.capture('landed_on_smart_fix_page', {
      productType: this.orbitClaimService?.state?.claimData?.bundle?.productType,
      isHeatingPlan: this.orbitClaimService?.state?.claimData?.bundle?.goodsColour === 'H',
      brand: this.orbitClaimService?.state?.claimData?.bundle?.manufacturer,
      solution: this.solutionSelected,
    });

    this.trackSmartFixGAOnLoad();

    this.generateDisclaimer();

    const bundleValue = sessionStorage.getItem(ORBIT_BUNDLE);
    const bundleKey = sessionStorage.getItem(ORBIT_BUNDLE_SESSION_KEY);
    // const planNumber = sessionStorage.getItem(PLAN_NUMBER);

    if (bundleValue) {
      if (!this.orbitClaimService.state.claimData) {
        this.orbitClaimService.handleLoadedInData(JSON.parse(bundleValue));
      }
      return;
    }

    if (bundleKey) {
      this.orbitClaimService.loadBundle(bundleKey!, false);
      return;
    }

    this.goToMMR();
  }

  onModeSelected(event: any): void {
    this.solutionSelected = event;
  }

  continueToBookingForm(): void {
    this.smartFixAnalyticsService.triggerSmartFixEvent(
      'No, book an engineer',
      'fault-details-completed',
      'disclaimer',
      'SF genAI',
      'claims-sf-genAI-disclaimer'
    );
    this.orbitClaimService.moveStepForward('smart-fix');

    Tracking.capture('continue_to_booking_form_from_smart_fix', {
      productType: this.orbitClaimService?.state?.claimData?.bundle?.productType,
      isHeatingPlan: this.orbitClaimService?.state?.claimData?.bundle?.goodsColour === 'H',
      brand: this.orbitClaimService?.state?.claimData?.bundle?.manufacturer,
      solution: this.solutionSelected || 'unknown',
    });
  }

  onTrySmartFix(): void {
    Tracking.capture('clicked_try_CTA', {
      productType: this.orbitClaimService?.state?.claimData?.bundle?.productType,
      isHeatingPlan: this.orbitClaimService?.state?.claimData?.bundle?.goodsColour === 'H',
      brand: this.orbitClaimService?.state?.claimData?.bundle?.manufacturer,
      solution: this.solutionSelected || 'unknown',
    });
    if (!this.showInteractionModeDialog) {
      this.smartFixAnalyticsService.pageViewEvent('claims-sf-virtual-assistant');

      const activeHero = this.userIsOnMobile ? this.mobileHero : this.desktopHero;
      if (activeHero()) {
        activeHero()?.playTransition();
        this.transitionPlaying = true;
        return;
      }
    } else {
      // Open dialog asking for user to confirm interaction mode - dialog visibility controlled by FF
      this.smartFixAnalyticsService.triggerSmartFixEvent(
        'popup presented',
        '',
        'voice chat presented',
        'SF genAI',
        'claims-sf-genAI'
      );
      this.dialogService
        .openDialog(Voice2VoiceDialogComponent, {
          data: {
            ariaRole: 'dialog',
            titleText: 'Try voice chat',
            descriptionText: `You can now talk to Smart-fix rather than typing. We’ll need microphone access so you can talk. Your audio is only used to help with this repair, nothing is stored`,
            iconName: 'waveform-lines',
            firstButton: { text: 'Use voice mode', variant: 'primary', mode: 'Voice to Voice' },
            secondButton: { text: 'Continue with text', variant: 'secondary', mode: 'Text' },
          },
        })
        .closed.subscribe((result: unknown) => {
          if (result == null) {
            Tracking.capture('smart_fix_close_interaction_mode_dialog', {
              productType: this.orbitClaimService?.state?.claimData?.bundle?.productType,
              isHeatingPlan: this.orbitClaimService?.state?.claimData?.bundle?.goodsColour === 'H',
              brand: this.orbitClaimService?.state?.claimData?.bundle?.manufacturer,
              solution: this.solutionSelected || 'unknown',
            });
          }

          const mode = (result as { mode?: 'Voice to Voice' | 'Text' } | null)?.mode;

          Tracking.capture('smart_fix_viewed_popup_voice2voice', {
            productType: this.orbitClaimService?.state?.claimData?.bundle?.productType,
            brand: this.orbitClaimService?.state?.claimData?.bundle?.manufacturer,
          });

          if (mode === 'Voice to Voice') {
            this.smartFixAnalyticsService.triggerSmartFixEvent(
              'Use voice to chat',
              '',
              'voice chat try',
              'SF genAI',
              'claims-sf-genAI'
            );
            Tracking.capture('smart_fix_start_voice2voice', {
              productType: this.orbitClaimService?.state?.claimData?.bundle?.productType,
              brand: this.orbitClaimService?.state?.claimData?.bundle?.manufacturer,
            });
            void this.initiateSmartFixVoiceToVoiceClient(true);
            return;
          }

          if (mode === 'Text') {
            this.interactionModeSelected = 'Text';
            this.waitingForFirstNovaSonicResponse = false;

            this.smartFixAnalyticsService.triggerSmartFixEvent(
              'Continue with text chat',
              '',
              'voice chat try',
              'SF genAI',
              'claims-sf-genAI'
            );
            this.initiateSmartFixClient();
          }
        });
      return;
    }

    this.initiateSmartFixClient();
  }

  onTransitionComplete(): void {
    this.transitionPlaying = false;
    this.initiateSmartFixClient();
  }

  async initiateSmartFixVoiceToVoiceClient(waitForFirstResponse = false): Promise<void> {
    this.interactionModeSelected = 'Voice to Voice';
    this.solutionSelected = 'Voice to Voice';
    this.novaSonicInitFailureHandled = false;
    let voiceInitialisationFailed = false;

    this.chatboxActive = true;
    this.scrollToTop();

    this.chatSession = this.createSession('bedrock');
    this.voiceChatSession = this.createSession('voice');

    Tracking.capture('on_try_smart_fix', {
      productType: this.orbitClaimService?.state?.claimData?.bundle?.productType,
      isHeatingPlan: this.orbitClaimService?.state?.claimData?.bundle?.goodsColour === 'H',
      brand: this.orbitClaimService?.state?.claimData?.bundle?.manufacturer,
      solution: this.solutionSelected || 'unknown',
    });

    if (waitForFirstResponse) {
      this.waitingForFirstNovaSonicResponse = true;
      try {
        await this.startNovaSonicConversation();
      } catch (error) {
        console.error('Nova Sonic failed to start voice conversation:', error);
        voiceInitialisationFailed = true;
        this.showNovaSonicInitialisationErrorAndSwitch();
      } finally {
        this.waitingForFirstNovaSonicResponse = false;
      }
    }

    if (voiceInitialisationFailed) {
      return;
    }

    Tracking.capture('smart_fix_initialise_solution', {
      productType: this.orbitClaimService?.state?.claimData?.bundle?.productType,
      isHeatingPlan: this.orbitClaimService?.state?.claimData?.bundle?.goodsColour === 'H',
      brand: this.orbitClaimService?.state?.claimData?.bundle?.manufacturer,
      solution: this.solutionSelected || 'unknown',
    });
  }

  private async startNovaSonicConversation(): Promise<void> {
    const wsUrl = AppInfo.Environment.novaSonicWsUrl;
    if (!wsUrl) {
      throw new Error('Nova Sonic URL is not configured');
    }

    const bundle = this.orbitClaimService?.state?.claimData?.bundle;
    await this.novaSonicVoiceService.start(wsUrl, {
      productType: bundle?.productType ?? '',
      brand: this.vm.brand ?? '',
      planNumber: this.planNumber,
    });

    // Only waited on for timing here (to know when to stop showing the
    // loading state) — NOT inserted into chatHistory. SmartFixVoiceChatComponent
    // has its own textOutput$ subscription (already live by this point, since
    // it mounts as soon as waitingForFirstNovaSonicResponse flips true, well
    // before the WebSocket/mic setup above resolves) that emits this same
    // event through (addMessage) → onAddMessage. Inserting it again here
    // duplicated the greeting — same messageId, two writers.
    await this.waitForFirstNovaSonicTextOutput();
  }

  private showNovaSonicInitialisationErrorAndSwitch(): void {
    if (
      this.novaSonicInitErrorDialogOpen ||
      this.novaSonicInitFailureHandled ||
      this.interactionModeSelected !== 'Voice to Voice'
    ) {
      return;
    }

    this.novaSonicInitErrorDialogOpen = true;

    const dialogRef = this.dialogService.openDialog(Voice2VoiceDialogComponent, {
      data: {
        ariaRole: 'alertdialog',
        titleText: 'Voice mode unavailable',
        descriptionText: `We could not connect to voice mode. You can continue in text mode.`,
        iconName: 'exclamation',
        isErrorDialog: true,
        autoSwitchCountdownSeconds: 8,
        autoSwitchCountdownPrefix: 'We will switch you over in',
        firstButton: { text: 'Switch to text mode', variant: 'primary', mode: 'Text' },
      },
      autoFocus: false,
    });
    this.novaSonicInitErrorDialogRef = dialogRef;

    // close after 8 seconds
    this.novaSonicInitErrorAutoSwitchTimer = setTimeout(() => {
      dialogRef.close({ mode: 'Text' });
    }, 8000);

    dialogRef.closed.subscribe(() => {
      this.clearNovaSonicInitErrorAutoSwitchTimer();
      this.novaSonicInitErrorDialogRef = null;
      this.novaSonicInitErrorDialogOpen = false;

      this.switchToTextModeAfterFailedVoiceInit();
    });
  }

  private switchToTextModeAfterFailedVoiceInit(): void {
    if (this.novaSonicInitFailureHandled) {
      return;
    }

    this.novaSonicInitFailureHandled = true;
    this.clearNovaSonicInitErrorAutoSwitchTimer();
    this.novaSonicInitErrorDialogRef?.close({ mode: 'Text' });
    this.novaSonicInitErrorDialogRef = null;
    this.novaSonicInitErrorDialogOpen = false;

    Tracking.capture('smart_fix_error_starting_voice2voice', {
      productType: this.orbitClaimService?.state?.claimData?.bundle?.productType,
      brand: this.orbitClaimService?.state?.claimData?.bundle?.manufacturer,
    });

    // switch UI immediately; stop voice session in the background.
    this.onInteractionModeChanged('Text');
    void this.novaSonicVoiceService.stop();
  }

  private clearNovaSonicInitErrorAutoSwitchTimer(): void {
    if (!this.novaSonicInitErrorAutoSwitchTimer) {
      return;
    }

    clearTimeout(this.novaSonicInitErrorAutoSwitchTimer);
    this.novaSonicInitErrorAutoSwitchTimer = null;
  }

  // once first response from nova sonic is received, we can show the voice chat UI
  // once first response from nova sonic is received, we can show the voice chat UI
  private waitForFirstNovaSonicTextOutput(): Promise<{ text: string; messageId: string }> {
    return new Promise((resolve, reject) => {
      const TIMEOUT_MS = 15_000;

      const textSub = this.novaSonicVoiceService.textOutput$.subscribe((payload) => {
        clearTimeout(timer);
        errorSub.unsubscribe();
        textSub.unsubscribe();
        resolve(payload);
      });

      // If the WebSocket errors or closes before Nova Sonic speaks, reject
      // immediately so the dialog can clear its loading state and let the
      // error handler (showNovaSonicErrorAndSwitch) take over.
      const errorSub = this.novaSonicVoiceService.error$.subscribe((err) => {
        clearTimeout(timer);
        textSub.unsubscribe();
        errorSub.unsubscribe();
        reject(new Error(err));
      });

      // Hard deadline — guards against a silent backend stall where no
      // textOutput and no error ever arrive.
      const timer = setTimeout(() => {
        textSub.unsubscribe();
        errorSub.unsubscribe();
        reject(new Error('Timed out waiting for Nova Sonic response'));
      }, TIMEOUT_MS);
    });
  }

  initiateSmartFixClient() {
    this.waitingForFirstNovaSonicResponse = false;
    this.chatboxActive = true;
    const solution =
      this.interactionModeSelected === 'Voice to Voice'
        ? 'Voice to Voice'
        : this.solutionSelected || (this.shouldUseAgenticSolution ? 'agent-llm' : 'rag');

    Tracking.capture('on_try_smart_fix', {
      productType: this.orbitClaimService?.state?.claimData?.bundle?.productType,
      isHeatingPlan: this.orbitClaimService?.state?.claimData?.bundle?.goodsColour === 'H',
      brand: this.orbitClaimService?.state?.claimData?.bundle?.manufacturer,
      solution,
    });
    this.trackGASmartFixInitialisation();

    this.chatSession = this.createSession('bedrock');

    this.initiateSmartFixSolution(solution);

    Tracking.capture('smart_fix_initialise_solution', {
      productType: this.orbitClaimService?.state?.claimData?.bundle?.productType,
      isHeatingPlan: this.orbitClaimService?.state?.claimData?.bundle?.goodsColour === 'H',
      brand: this.orbitClaimService?.state?.claimData?.bundle?.manufacturer,
      solution: this.interactionModeSelected === 'Text' ? this.solutionSelected || 'unknown' : 'Voice to Voice',
    });
  }

  initiateSmartFixSolution(solution: string): void {
    if (solution === 'Voice to Voice') return;
    switch (solution) {
      case 'Text':
      case 'agent-llm':
        this.showGreeting = true;
        this.startSafetyCheck();
        break;
      case 'rag':
        this.showGreeting = true;
        this.startTriageQuestions();
        break;
      case 'chat-with-docs':
        this.chatSession?.startSession(() => {
          this.isFirstInteraction.set(true);
        }, solution);
        break;
      default:
        this.chatSession?.startSession(() => {
          this.isFirstInteraction.set(true);
        }, solution);
        break;
    }
  }

  onAddMessage(message: BedrockMessages) {
    const history = this.chatHistory();

    if (message.messageId && history.some((existing) => existing.messageId === message.messageId)) {
      return;
    }

    // covers the case where the duplicate turn arrives with
    // a different messageId
    const last = history[history.length - 1];
    if (last && last.role === message.role && last.content.message === message.content.message) {
      return;
    }

    if (message.role === 'user') {
      this.userMessageCount.update((count) => count + 1);
    }

    this.chatHistory.set([
      ...history,
      {
        role: message.role,
        sessionId: this.sessionId(),
        messageId: message.messageId,
        content: {
          message: message.content.message,
          type: message.content.type,
          timestamp: new Date().toISOString(),
          image: message.content.image,
          audio: message.content.audio,
        },
      } as BedrockMessages,
    ]);
  }

  onUpdateMessageBasedOnRoleMerge(messageId: string, updatedMessage: BedrockMessages) {
    const { content } = updatedMessage;
    const { audio } = content;
    const updatedHistory = this.chatHistory().map((msg) => {
      if (msg.messageId === messageId) {
        return {
          ...msg,
          content: {
            ...msg.content,
            message: updatedMessage.content.message,
            type: updatedMessage.content.type,
            timestamp: new Date().toISOString(),
            image: updatedMessage.content.image,
            audio,
          },
        };
      }
      return msg;
    });
    this.chatHistory.set(updatedHistory);
  }

  onUpdateMessageById(messageId: string, updatedMessage: BedrockMessages) {
    // here should go the transcribe logic
    const { content } = updatedMessage;
    const { audio } = content;
    const updatedHistory = this.chatHistory().map((msg) => {
      if (msg.messageId === messageId) {
        return {
          ...msg,
          content: {
            ...msg.content,
            message: updatedMessage.content.message,
            type: updatedMessage.content.type,
            timestamp: new Date().toISOString(),
            image: updatedMessage.content.image,
            audio,
          },
        };
      }
      return msg;
    });
    this.chatHistory.set(updatedHistory);
  }

  onSessionIdChanged(newSessionId: string): void {
    this.sessionId.set(newSessionId);
  }

  moveToFeedback(): void {
    this.waitingForFirstNovaSonicResponse = false;
    // Track end chat analytics

    Tracking.capture('smart_fix_end_chat', {
      productType: this.orbitClaimService?.state?.claimData?.bundle?.productType,
      isHeatingPlan: this.orbitClaimService?.state?.claimData?.bundle?.goodsColour === 'H',
      brand: this.orbitClaimService?.state?.claimData?.bundle?.manufacturer,
      solution: this.solutionSelected || 'unknown',
    });
    this.smartFixAnalyticsService.triggerSmartFixEvent(
      'clicked',
      '',
      'end chat',
      'smart-fix-virtual-assistant',
      'claims-sf-virtual-assistant'
    );

    if (this.userMessageCount() > 0) {
      this.chatboxActive = false;
      this.feedbackActive = true;
    } else {
      this.goToMMR();
    }
  }

  goToMMR(navigator: { href: string } = window.location): void {
    navigator.href = this.MMRlink;
  }

  private scrollToTop(): void {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }

  onContinueChat(): void {
    this.isWarningDialog = false;
  }

  enableFeedbackInput() {
    this.askForFeedback = true;
  }

  onSendFeedback(event: any) {
    this.feedbackText = event;
    this.chatMessage = '';
  }

  onRequestFeedback() {
    Tracking.capture('smart_fix_on_request_feedback', {
      productType: this.orbitClaimService?.state?.claimData?.bundle?.productType,
      isHeatingPlan: this.orbitClaimService?.state?.claimData?.bundle?.goodsColour === 'H',
      brand: this.orbitClaimService?.state?.claimData?.bundle?.manufacturer,
      solution: this.solutionSelected || 'unknown',
    });
    this.feedbackRequested = true;
  }

  attemptEnd(): void {
    this.smartFixAnalyticsService.triggerSmartFixEvent(
      'nav menu',
      'fault-details-completed',
      'end-chatbot-x-clicked',
      'SF genAI',
      'claims-sf-genAI'
    );
    if (this.interactionStarted) {
      this.askForFeedback = true;
    } else {
      this.isWarningDialog = true;
    }
  }

  startSafetyCheck(): void {
    // First show safety check question before triage questions
    Tracking.capture('smart_fix_start_safety_check', {
      productType: this.orbitClaimService?.state?.claimData?.bundle?.productType,
      isHeatingPlan: this.orbitClaimService?.state?.claimData?.bundle?.goodsColour === 'H',
      brand: this.orbitClaimService?.state?.claimData?.bundle?.manufacturer,
      solution: this.solutionSelected || 'unknown',
    });
    this.showSafetyCheck = true;
    const safetyCheckMessage = {
      role: 'agent' as const,
      sessionId: this.sessionId() || 'unknown',
      content: {
        message: "Before we begin, let's run a safety check. Do you notice a burning smell or smoke?",
        type: 'text',
        timestamp: new Date().toISOString(),
      },
    };
    this.chatHistory.update((history) => [...history, safetyCheckMessage]);
  }

  startTriageQuestions(): void {
    Tracking.capture('smart_fix_start_triage_questions', {
      productType: this.orbitClaimService?.state?.claimData?.bundle?.productType,
      isHeatingPlan: this.orbitClaimService?.state?.claimData?.bundle?.goodsColour === 'H',
      brand: this.orbitClaimService?.state?.claimData?.bundle?.manufacturer,
      solution: this.solutionSelected || 'unknown',
    });
    this.gatheringContextActive = true;
    this.chatSession
      ?.gatherContextFromUser({
        answers: this.answers as { question: string; answer: string }[],
        productType: this.vm.productType,
        brand: this.vm.bundle?.mandatoryData?.bookingOEM,
        sessionId: this.sessionId() || '',
      })
      .then((res) => {
        this.currentPrompt = res.reAsk || res.nextQuestion || '';
        this.answers = res.answers || [];
        this.gatheringContextActive = !res.done;
        this.chatHistory.update((history) => [
          ...history,
          {
            role: 'agent',
            sessionId: this.sessionId() || 'unknown',
            content: {
              message: this.currentPrompt,
              type: 'text',
              timestamp: new Date().toISOString(),
            },
          },
        ]);
        if (res.done) {
          this.summary = res?.summary || '';
          this.gatheringContextActive = false;
          this.showImagePrompt = true;
        }

        if (!this.vm.sessionId) {
          this.sessionId.set(res.sessionId || 'unknown');
        }
      });
  }

  onContextGatheringStepDone() {
    this.gatheringContextActive = false;
  }

  onSafetyCheckCompleted() {
    this.showSafetyCheck = false;
    // Now make the first gatherContextFromUser call after safety check
    this.startTriageQuestions();
  }

  async onRecordFireSafetyCheck() {
    await this.chatSession?.recordFireSafetyCheck(this.planNumber);
  }

  trackSmartFixGAOnLoad(): void {
    this.smartFixAnalyticsService.pageViewEvent('claims-sf-genAI');

    this.smartFixAnalyticsService.triggerSmartFixEvent(
      'banner presented',
      'fault-details-completed',
      'banner - genAI',
      'SF genAI',
      'claims-sf-genAI'
    );
  }

  trackGASmartFixInitialisation(): void {
    this.smartFixAnalyticsService.triggerSmartFixEvent(
      'Yes',
      'fault-details-completed',
      'banner - genAI',
      'SF genAI',
      'claims-sf-genAI'
    );

    // comment out the below lines for now as per analytics team request

    // this.smartFixAnalyticsService.pageViewEvent('claims-sf-genAI-disclaimer');

    // this.smartFixAnalyticsService.triggerSmartFixEvent(
    //   'presented',
    //   'fault-details-completed',
    //   'disclaimer',
    //   'SF genAI',
    //   'claims-sf-genAI'
    // );

    // this.smartFixAnalyticsService.triggerSmartFixEvent(
    //   'Yes, I understand',
    //   'fault-details-completed',
    //   'disclaimer',
    //   'SF genAI',
    //   'claims-sf-genAI-disclaimer'
    // );
  }

  generateDisclaimer(): void {
    const generalDisclaimer =
      "Please follow the instructions carefully. We take responsibility if you follow them correctly and they're not right. But we're not responsible for any issues if this guidance isn't followed properly.";
    const prefix = this.vm.isHeatingPlan
      ? 'Important: Please avoid opening the boiler cover or doing anything that interferes with the gas supply. '
      : 'Disclaimer: ';

    this.hints = [
      `${prefix}${generalDisclaimer}`,
      'For more help and safety information, check the "warnings" section in your appliance manual.',
      "If our virtual assistant can't solve it, you can still book an engineer. Give it a try?",
    ];
  }

  /// Functions for handling the in-flight switch from Voice to Voice to Text
  private listenForNovaSonicModelTimeout(): void {
    this.novaSonicVoiceService.modelTimeout$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.interactionModeSelected !== 'Voice to Voice') {
        return;
      }

      this.dialogService
        .openDialog(Voice2VoiceDialogComponent, {
          data: {
            ariaRole: 'alertdialog',
            titleText: 'This voice session has ended',
            descriptionText: `Voice sessions last 8 minutes. Switch to text chat to carry on where you left off, or continue with your repair booking.`,
            iconName: 'clock',
            firstButton: { text: 'Switch to text chat', variant: 'secondary', mode: 'Text' },
            secondButton: {
              text: 'Continue repair booking',
              variant: 'secondary',
              action: 'repair_booking',
            },
          },
        })
        .closed.subscribe((result: unknown) => {
          const dialog = result as { mode?: InteractionMode; action?: DialogAction } | null;
          if (dialog?.action === 'repair_booking') {
            this.moveToFeedback();
          } else if (dialog?.mode === 'Text') {
            // switch to text while maintaining voice context
            this.onInteractionModeChanged('Text');
          }
        });
    });
  }

  onInteractionModeChanged(mode: 'Voice to Voice' | 'Text'): void {
    if (mode === 'Text' && this.novaSonicInitErrorDialogRef) {
      this.novaSonicInitErrorDialogRef.close({ mode: 'Text' });
    }

    if (this.interactionModeSelected === mode) {
      return;
    }

    if (mode !== 'Voice to Voice') {
      this.waitingForFirstNovaSonicResponse = false;
    }

    const previousMode = this.interactionModeSelected;
    const switchedFromVoiceToText = this.chatboxActive && previousMode === 'Voice to Voice' && mode === 'Text';
    this.interactionModeSelected = switchedFromVoiceToText ? 'Text' : mode;

    this.solutionSelected = mode === 'Text' ? (this.shouldUseAgenticSolution ? 'agent-llm' : 'rag') : 'Voice to Voice';

    if (mode === 'Voice to Voice' && previousMode !== 'Voice to Voice') {
      this.scrollToTop();
    }

    // if voice mode was never actually started, run the standard Text journey.
    if (switchedFromVoiceToText && !this.hasStartedVoiceConversation()) {
      this.interactionModeSelected = mode;
      this.initiateSmartFixClient();
      return;
    }

    if (switchedFromVoiceToText) {
      this.startConversationAfterModeSwitch();
      return;
    }

    this.interactionModeSelected = mode;
    this.initiateSmartFixClient();
  }

  private hasStartedVoiceConversation(): boolean {
    // Voice messages are tagged 'text' (agent) / 'voice_to_text' (user), never
    // 'voice_to_voice' — and by the time this runs, the voice connection has
    // already been stopped by the caller, so status() can't be trusted either.
    // Any accumulated transcript means the voice session actually produced
    // messages worth continuing from.
    return this.chatHistory().length > 0;
  }

  private startConversationAfterModeSwitch(): void {
    // Safe to read even after modelTimeout/disconnect already tore the session down —
    // nova-sonic-voice.service only clears connectionId when the next session starts.
    const voiceSessionId = this.novaSonicVoiceService.getConnectionId() ?? undefined;
    void this.novaSonicVoiceService.stop();
    this.gatheringContextActive = false;
    this.showSafetyCheck = false;
    this.showGreeting = false;

    const currentSessionId = this.sessionId() || '';
    const voice2VoiceTranscript = this.chatHistory()
      .map((msg) => `${msg.role}: ${msg.content.message}`)
      .join('\n');

    if (!this.chatSession || !voice2VoiceTranscript.trim()) {
      // Nothing to hand over — switch straight to the standard text journey.
      this.fallbackToTextModeConversation();
      return;
    }

    // this.summary is only ever populated by the text-triage gathering-context
    // flow, which a pure voice-to-voice session never runs — so on a voice
    // fallback it's always empty. Fall back to the caller's last spoken
    // message so the stored conversation doesn't start with a blank entry.
    const lastVoiceUserMessage = [...this.chatHistory()].reverse().find((msg) => msg.role === 'user')?.content.message;
    const summaryForFallback = this.summary || lastVoiceUserMessage || 'Continued from voice conversation';

    // Start the handover request, but move to text UI immediately.
    this.isSwitchingFromVoiceToText = false;
    const agentPromise = this.chatSession.startAgentConversation(
      summaryForFallback,
      this.vm.brand,
      currentSessionId,
      this.vm.productType,
      voice2VoiceTranscript,
      voiceSessionId
    );

    this.interactionModeSelected = 'Text';
    this.chatLoading.set(true);

    agentPromise
      .then((agentRes: any) => {
        const agentMessage = agentRes?.summary || agentRes?.message || agentRes?.response;
        if (agentMessage) {
          this.chatHistory.update((history) => [
            ...history,
            {
              role: 'agent',
              sessionId: this.sessionId() || currentSessionId || 'unknown',
              content: {
                message: agentMessage,
                type: 'text',
                timestamp: new Date().toISOString(),
              },
            } as BedrockMessages,
          ]);
        }
      })
      .catch((error) => {
        console.error('Failed to continue conversation from voice:', error);
        // Silent fail — the voice transcript is already visible in the chat,
        // so the user still has full context even without the handover message.
      })
      .finally(() => {
        this.chatLoading.set(false);
      });
  }

  private fallbackToTextModeConversation(): void {
    this.isSwitchingFromVoiceToText = false;
    this.interactionModeSelected = 'Text';
    this.initiateSmartFixClient();
  }

  private createSession(type: 'bedrock'): BedrockChatSession;
  private createSession(type: 'voice'): VoiceChatSession;
  private createSession(type: 'bedrock' | 'voice'): BedrockChatSession | VoiceChatSession {
    const context = {
      productType: this.vm.productType,
      planNumber: this.route.snapshot.params['planNumber'],
      uploadSessionId: '',
    };

    if (type === 'voice') {
      return SmartFixClient.initialise('voice')(
        { appName: 'bright-ui', environment: AppInfo.Environment.smartFixVoiceApi },
        context,
        (loading) => this.chatLoading.set(loading),
        (error) => {
          console.error('Smart fix Voice to Voice API error:', error);
          this.chatLoading.set(false);
        }
      );
    }

    return SmartFixClient.initialise('bedrock')(
      { appName: 'bright-ui', environment: AppInfo.Environment.smartFixApi },
      context,
      (loading) => this.chatLoading.set(loading),
      (response) => {
        this.sessionId.set(response.sessionId);
        this.chatHistory.update((val) => {
          val.push(response);
          return val;
        });
      },
      (error) => {
        console.error('Smart fix API error:', error);
        this.chatLoading.set(false);
      }
    );
  }

  get uiState(): SmartFixUIState {
    if (this.chatboxActive || this.waitingForFirstNovaSonicResponse) return SmartFixUIState.Chat;
    if (this.feedbackActive) return SmartFixUIState.Feedback;
    return SmartFixUIState.Idle;
  }

  get titleAndButtonsState() {
    if (this.chatboxActive) {
      return {
        title: this.showBrokenTitle
          ? 'Smart-fix,<br> your virtual assistant </br>'
          : 'Smart-fix, your virtual assistant',
        buttonTitle: this.userIsOnMobile ? 'End chat' : 'Back to repairs page',
        backIconClass: this.userIsOnMobile ? 'fa-regular fa-xmark fa-lg' : 'fa-regular fa-arrow-left fa-lg',
        link: this.userIsOnMobile ? '#feedback' : this.MMRlink,
      };
    } else if (this.isFeedbackMode || this.feedbackActive) {
      return {
        title: 'Before you go, please tell us what you thought',
        buttonTitle: 'Back to repairs page',
        backIconClass: 'fa-regular fa-arrow-left fa-lg',
        link: this.MMRlink,
      };
    } else {
      return {
        title: `Don't wait for an engineer, try Smart-fix today`,
        buttonTitle: 'Back to repairs page',
        backIconClass: 'fa-regular fa-arrow-left fa-lg',
        link: this.MMRlink,
      };
    }
  }

  get endChatButtonState() {
    return {
      buttonTitle: 'End chat',
      backIconClass: 'fa-regular fa-xmark fa-lg',
      action: () => this.moveToFeedback(),
    };
  }

  get userIsOnMobile(): boolean {
    return window.innerWidth < Breakpoints.sm;
  }

  get showBrokenTitle(): boolean {
    return window.innerWidth < Breakpoints.md;
  }

  get showSmartFixImage(): boolean {
    return !this.userIsOnMobile || (this.userIsOnMobile && !this.chatboxActive);
  }
}
